import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sun, Loader2, Star, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RASHIS = [
  { name: 'Mesh', nameHi: 'मेष', english: 'Aries', icon: '♈', color: '#FF6B6B' },
  { name: 'Vrishabh', nameHi: 'वृषभ', english: 'Taurus', icon: '♉', color: '#4ECDC4' },
  { name: 'Mithun', nameHi: 'मिथुन', english: 'Gemini', icon: '♊', color: '#FFE66D' },
  { name: 'Kark', nameHi: 'कर्क', english: 'Cancer', icon: '♋', color: '#95E1D3' },
  { name: 'Singh', nameHi: 'सिंह', english: 'Leo', icon: '♌', color: '#F38181' },
  { name: 'Kanya', nameHi: 'कन्या', english: 'Virgo', icon: '♍', color: '#AA96DA' },
  { name: 'Tula', nameHi: 'तुला', english: 'Libra', icon: '♎', color: '#FCBAD3' },
  { name: 'Vrishchik', nameHi: 'वृश्चिक', english: 'Scorpio', icon: '♏', color: '#A8D8EA' },
  { name: 'Dhanu', nameHi: 'धनु', english: 'Sagittarius', icon: '♐', color: '#FF9F43' },
  { name: 'Makar', nameHi: 'मकर', english: 'Capricorn', icon: '♑', color: '#6C5CE7' },
  { name: 'Kumbh', nameHi: 'कुंभ', english: 'Aquarius', icon: '♒', color: '#00CEC9' },
  { name: 'Meen', nameHi: 'मीन', english: 'Pisces', icon: '♓', color: '#FD79A8' },
];

export default function DailyRashifalPage({ user }) {
  const navigate = useNavigate();
  const [selectedRashi, setSelectedRashi] = useState(null);
  const [rashifal, setRashifal] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchRashifal = async (rashi) => {
    setSelectedRashi(rashi);
    setLoading(true);
    setRashifal(null);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.post(`${API}/jyotish/rashifal`, { rashi: rashi.english }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRashifal(res.data);
    } catch { setRashifal({ error: true }); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50" data-testid="rashifal-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B2500] via-[#A0522D] to-[#8B4513] text-white py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate('/jyotish')} className="flex items-center gap-1 text-orange-200 text-sm mb-3">
            <ArrowLeft className="w-4 h-4" /> Back to Jyotish
          </button>
          <div className="flex items-center gap-2">
            <Sun className="w-7 h-7 text-yellow-300" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'Yatra One', serif" }}>Daily Rashifal</h1>
          </div>
          <p className="text-orange-200 text-sm mt-1">Select your rashi to view today's horoscope</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Rashi Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-6">
          {RASHIS.map(r => (
            <button
              key={r.name}
              onClick={() => fetchRashifal(r)}
              className={`flex flex-col items-center gap-1.5 py-4 rounded-2xl border-2 transition-all ${
                selectedRashi?.name === r.name
                  ? 'border-[#8B2500] bg-orange-50 shadow-md scale-105'
                  : 'border-slate-100 bg-white hover:border-orange-200 hover:shadow-sm'
              }`}
              data-testid={`rashi-${r.english.toLowerCase()}`}
            >
              <span className="text-2xl">{r.icon}</span>
              <span className="text-xs font-bold text-slate-700">{r.nameHi}</span>
              <span className="text-[10px] text-slate-400">{r.english}</span>
            </button>
          ))}
        </div>

        {/* Result */}
        {loading && (
          <div className="flex flex-col items-center py-12">
            <Loader2 className="w-8 h-8 text-[#8B2500] animate-spin mb-3" />
            <p className="text-sm text-slate-500">Reading the stars for {selectedRashi?.nameHi}...</p>
          </div>
        )}

        {rashifal && !rashifal.error && !loading && (
          <div className="bg-white rounded-2xl shadow-md border border-orange-100 overflow-hidden" data-testid="rashifal-result">
            <div className="px-5 py-4 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{selectedRashi.icon}</span>
                <div>
                  <h3 className="font-bold text-[#8B2500] text-lg">{selectedRashi.nameHi} ({selectedRashi.english})</h3>
                  <p className="text-xs text-slate-500">Today's Horoscope</p>
                </div>
              </div>
              <button onClick={() => fetchRashifal(selectedRashi)} className="p-2 rounded-full hover:bg-orange-100">
                <RefreshCw className="w-4 h-4 text-[#8B2500]" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              {rashifal.overall && <Section title="Overall" text={rashifal.overall} />}
              {rashifal.career && <Section title="Career" text={rashifal.career} />}
              {rashifal.love && <Section title="Love & Relationships" text={rashifal.love} />}
              {rashifal.health && <Section title="Health" text={rashifal.health} />}
              {rashifal.lucky_number && (
                <div className="flex items-center gap-4 pt-2 border-t border-orange-50">
                  <div className="text-center">
                    <p className="text-[10px] text-slate-400 uppercase">Lucky Number</p>
                    <p className="text-lg font-bold text-[#8B2500]">{rashifal.lucky_number}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-slate-400 uppercase">Lucky Color</p>
                    <p className="text-sm font-semibold text-[#8B2500]">{rashifal.lucky_color}</p>
                  </div>
                  {rashifal.rating && (
                    <div className="text-center ml-auto">
                      <p className="text-[10px] text-slate-400 uppercase">Rating</p>
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < rashifal.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'}`} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {rashifal?.error && !loading && (
          <div className="text-center py-12">
            <p className="text-red-500">Failed to fetch rashifal. Please try again.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, text }) {
  return (
    <div>
      <h4 className="text-xs font-bold text-[#8B2500] uppercase tracking-wide mb-1">{title}</h4>
      <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
    </div>
  );
}
